import { Navigate, Link } from 'react-router-dom'
import { Button, InlineAlert, UniversalPageLayout } from '../components/BaseComponents'
import { routes } from '../components/Router/routes'
import useUserData from '../hooks/useUserData'
import { useContext, useState } from 'react'
import { userContext } from '../context/UserContext'
import OrganisationUnlockRequestModal from '../components/OrganisationUnlockRequestModal/OrganisationUnlockRequestModal'
import { MembershipSummary } from './SelectOrganisation'
import { isSampleState } from '../lib/sampleState'
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus'
import PendingInvitationsBanner from '../components/PendingInvitationsBanner/PendingInvitationsBanner'

function CompanyDashboard() {
	const userData = useUserData();

	const { checkAccess } = useContext(userContext);
	const [unlockTarget, setUnlockTarget] = useState<MembershipSummary | null>(null);

	const sampleState = isSampleState(userData);
	const lockedOrganisations = (userData?.memberships ?? []).filter((membership) => membership.companyLocked);

	// The setup route requires 'user.write'; redirecting without it would loop back here
	if(userData?.hasCompletedSetup === false && checkAccess('user.write')) {
		return <Navigate to={routes.companySetup.path} replace />
	}


	const sampleBanners = sampleState && (lockedOrganisations.length > 0 || userData.canRequestUpgrade) ? (
		<>
			{lockedOrganisations.map((organisation) => (
				<InlineAlert key={organisation.companyId} type='warning' data-testid='locked-organisation-message'>
					<p>
						Your organisation {organisation.companyName} is currently locked. You are working in a sample
						organisation until it is unlocked.{' '}
						<button
							type='button'
							className={`text-link ${TEXT_LINK_FOCUS_RING}`}
							onClick={() => setUnlockTarget(organisation)}
						>
							Request unlock
						</button>
					</p>
				</InlineAlert>
			))}
			{userData.canRequestUpgrade && lockedOrganisations.length === 0 && (
				<InlineAlert type='info' data-testid='no-real-organisation-message'>
					<p>
						The features in your account are currently limited and you are working in a sample organisation.{' '}
						<Link to={routes.companySettings.path} className={`text-link ${TEXT_LINK_FOCUS_RING}`}>
							Request an upgrade in Account Settings
						</Link>
						. You will then be able to add your real organisation to Neoni®.
					</p>
				</InlineAlert>
			)}
		</>
	) : undefined;

	const banners = (
		<>
			<PendingInvitationsBanner />
			{sampleBanners}
		</>
	);

	return (
		<UniversalPageLayout
			testid='company-dashboard-main'
			documentTitle={routes.companyDashboard.name}
			heading={{ title: routes.companyDashboard.menuName }}
			banners={banners}
		>
			{unlockTarget && (
				<OrganisationUnlockRequestModal
					open
					companyId={unlockTarget.companyId}
					companyName={unlockTarget.companyName}
					onClose={() => setUnlockTarget(null)}
				/>
			)}
			<div className='p-lg bg-charcoal-600 rounded'>
				<h2 className='mt-3 text-4xl text-white'>
				Welcome to Neoni® Beta testing!
				</h2>
				<p className='mt-5 text-white text-lg'>
					You are a leader in emissions reduction and we are excited to have you with us.
				</p>
				<p className='my-lg text-white text-lg'>
					Neoni enables you to decarbonise your supply chain in three steps:
				</p>
				<p className='my-lg text-white pl-5'>
					1. Get real-world, reliable, data from your supply chain - in Beta
				</p>
				<p className='my-lg text-white pl-5'>
					2. Use Neoni insights to progressively reduce supply chain emissions - coming soon
				</p>
				<p className='my-lg text-white pl-5'>
					3. Use Neoni reporting to evidence the results and enhance reputation - coming soon
				</p>
				<p className='my-lg text-white text-lg'>Let’s get started by:</p>
				<ul className='my-lg text-white list-disc pl-9'>
					<li>
						Adding purchase data to calculate direct emissions and estimate supply chain
						emissions
					</li>
					<li>
						Then invite suppliers to use Neoni and share their summary emissions data with you
					</li>
				</ul>
				<div className='mt-5 mb-2'>
					<Button
						isLink={true}
						url={checkAccess('company.accounting') ? routes.companyPurchases.path : routes.myReports.path }
						type='Primary'
						text='Get Started!'
					/>
				</div>
			</div>
		</UniversalPageLayout>
	)
}
export default CompanyDashboard
